<script setup lang="ts">
import type { CmsElementProductListing } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { defu } from "defu";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCategoryListing } from "#imports";
import type { Schemas, operations } from "#shopware";
import {productListTracking} from "~/components/utilities/GtmTrackings";
import { nextTick } from "vue";
import { useHistoryNavigation } from '~/composables/useHistoryNavigation';

const props = defineProps<{
  content: CmsElementProductListing;
}>();

const defaultLimit = 15;
const defaultPage = 1;
const defaultOrder = "name-asc";
const productListElement = useTemplateRef("productListElement");

type Translations = {
  listing: {
    noProducts: string;
    perPage: string;
    product: string;
    products: string;
  };
};
let translations: Translations = {
  listing: {
    noProducts: "No products found 😔",
    perPage: "Per Page:",
    product: "Product",
    products: "Products",
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

const {
  changeCurrentPage,
  getCurrentPage,
  getElements,
  getTotalPagesCount,
  loading,
  setInitialListing,
} = useCategoryListing();
const route = useRoute();
const router = useRouter();
const limit = ref(
  route.query.limit
    ? Number(route.query.limit)
    : props.content?.data?.listing?.limit
      ? Number(props.content?.data?.listing?.limit)
      : defaultLimit
);

const initalRoute = defu(route);
watch(
  () => route,
  (newRoute) => {
    if (initalRoute.path !== newRoute.path) {
      return;
    }
    if (Object.keys(newRoute.query).length > 0) {
      return;
    }
    // this fires to reset the page when query are removed/empty on client side navigation for the same page (without hard reload)
    changeCurrentPage(defaultPage, {
      limit: defaultLimit,
      p: defaultPage,
      order: defaultOrder,
    } as unknown as operations["searchPage post /search"]["body"]);
  },
  { deep: true }
);

const changePage = async (page: number) => {
  window.scrollTo({ top: 0, behavior: "smooth" });

  await router.push({
    query: {
      ...route.query,
      p: page,
      limit: limit.value,
    },
  });

  await changeCurrentPage(
    page,
    route.query as unknown as operations["searchPage post /search"]["body"]
  );

  // Force Vue to update pagination count
  await nextTick();
};

const changeLimit = async (limit: Event) => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  const select = limit;

  await router.push({
    query: {
      ...route.query,
      limit: select.value,
      p: defaultPage,
    },
  });
  await changeCurrentPage(
    defaultPage,
    route.query as unknown as operations["searchPage post /search"]["body"]
  );
};

const isProductListing = computed(
  () => props.content?.type === "product-listing"
);

const changeSortingOrder = async (event: Event) => {
  currentSortingOrder.value = event.value
}
// This is a workaround because vercel caching with the nuxt preset does not support query params at the moment
// @see https://github.com/shopware/frontends/issues/687#issuecomment-1988392091
const compareRouteQueryWithInitialListing = async () => {
  const limitListing = props?.content?.data?.listing.limit ?? defaultLimit;
  const pageListing = props?.content?.data?.listing.page ?? defaultPage;
  const orderListing = props?.content?.data?.listing.sorting ?? defaultOrder;

  const isChangePageNeeded =
    (route.query.limit && limit.value !== limitListing) ||
    (route.query.p && Number(route.query.p) !== pageListing) ||
    (route.query.order && route.query.order !== orderListing);

  if (isChangePageNeeded) {
    const limitQuery = route.query.limit
      ? Number(route.query.limit)
      : defaultLimit;
    const pageQuery = route.query.p ? Number(route.query.p) : defaultPage;
    const orderQuery = route.query.order
      ? (route.query.order as string)
      : defaultOrder;
    const newQuery = {
      limit: limitQuery,
      p: pageQuery,
      order: orderQuery,
    };
    console.warn(
      "The current route does not match the initial listing. Changing the route to match the initial listing."
    );
    limit.value = limitQuery;
    await changeCurrentPage(
      pageQuery,
      newQuery as unknown as operations["searchPage post /search"]["body"]
    );
  }
};

setInitialListing(
  props?.content?.data?.listing as Schemas["ProductListingResult"]
);

compareRouteQueryWithInitialListing();

const { changeCurrentSortingOrder, getCurrentSortingOrder, getSortingOrders } =
  useCategoryListing();

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || "",
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
      },
    });

    await changeCurrentSortingOrder(order, {
      ...(route.query as unknown as operations["searchPage post /search"]["body"]),
      limit: route.query.limit ? Number(route.query.limit) : 15,
    });
  },
});

const sortingOptions = ref<[]>([
  {
    value: 12,
    label: "12",
  },
  {
    value: 24,
    label: "24",
  },
  {
    value: 48,
    label: "48",
  },
  {
    value: 96,
    label: "96",
  },
]);

const isLoading = ref(false);
const { historyState } = useHistoryNavigation()
onMounted(async () => {

  const isBack = historyState.isBackNavigation;
  const savedScrollPosition = sessionStorage.getItem('listingScrollPosition') 
    ? parseInt(sessionStorage.getItem('listingScrollPosition') || '0')
    : 0;

  if (isBack && savedScrollPosition) {
    isLoading.value = true;
    window.scrollTo({
        top: 0,
        behavior: 'auto'
    });

    // Add a temporary style to prevent any scrolling during the setup phase
    const styleElement = document.createElement('style');
    styleElement.id = 'temp-scroll-lock';
    styleElement.textContent = 'html, body { overflow: hidden !important; height: 100% !important; opacity: 0.75; }';
    document.head.appendChild(styleElement);
  }
   
  // For back navigation, restore scroll position
  if (isBack && savedScrollPosition) {
    // Small delay to ensure content is loaded
    setTimeout(() => {
      // Remove scroll lock for normal navigation
      const lockStyle = document.getElementById('temp-scroll-lock');
      if (lockStyle) {
        lockStyle.remove();
      }
      
      window.scrollTo({
        top: savedScrollPosition,
        behavior: 'auto'
      });
      isLoading.value = false;
    }, 100);
  }
   
  isLoading.value = false;

  productListTracking(getElements.value);
});
const savedScrollPositionTrigger = () => {
  if (typeof window !== 'undefined') {
    const scrollPosition = window.scrollY;
    sessionStorage.setItem('listingScrollPosition', scrollPosition.toString());
  }
};
</script>

<template>
  <div class="w-full">
    <div
      class="w-full md:w-auto flex flex-wrap justify-between gap-2 lg:gap-4 mb-2 md:mb-10 xl:-mt-12"
    >
      <div
        class="text-center place-self-center mt-2 lg:mt-0 ml-2 w-full md:w-auto mb-5"
      >
        <div
          class="md:flex gap-x-2 inline-block align-top text-center md:text-left w-full md:w-auto"
          data-testid="listing-pagination-limit-box"
        >
          <SharedSelect
            name="sort-option"
            v-model="getCurrentSortingOrder"
            @change="changeSortingOrder"
            :options="
              getSortingOrders.map((sorting) => ({
                value: sorting.key,
                label: sorting.label,
              }))
            "
          >
          </SharedSelect>

          <SharedSelect
            class="mt-3 md:mt-0"
            id="limit"
            v-model="limit"
            name="limitchoices"
            data-testid="listing-pagination-limit-select"
            @change="changeLimit"
            :options="sortingOptions"
          ></SharedSelect>
        </div>
      </div>
      <div class="mobile:mt-3">
        <SwPagination
          v-if="getTotalPagesCount > 1"
          :total="getTotalPagesCount"
          :current="Number(getCurrentPage)"
          @change-page="changePage"
        />
      </div>
    </div>

    <div
      class="product-wrapper flex flex-wrap -mx-2 w-[calc(100%+32px)] gap-y-6"
    >
      <div
        class="w-1/2 lg:w-1/3 xl:w-1/4"
        v-if="!loading"
        ref="productListElement"
        v-for="product in getElements"
        :key="product.id"
      >
        <ProductCard
          :product="product"
          :is-product-listing="isProductListing"
          @click="savedScrollPositionTrigger"
        />
      </div>
      <div
        v-if="loading"
        v-for="index in limit"
        :key="index"
        data-testid="loading"
        class="w-1/2 lg:w-1/3 xl:w-1/4 px-1 md:px-4"
      >
        <CmsSkeletonProductCardSkeleton class="w-full" />
      </div>
      <!-- <div v-else>
        <h2 class="mx-auto text-center">
          {{ translations.listing.noProducts }}
        </h2>
      </div> -->
    </div>
    <div
      class="w-full md:w-auto flex flex-wrap justify-end gap-2 lg:gap-4 mb-5"
    >
      <div class="mt-6">
        <SwPagination
          v-if="getTotalPagesCount > 1"
          :total="getTotalPagesCount"
          :current="Number(getCurrentPage)"
          @change-page="changePage"
        />
      </div>
    </div>
  </div>
</template>
<style>
html {
  scroll-behavior: smooth;
}
</style>